import { Controller } from '@hotwired/stimulus'
import 'bootstrap-datepicker'
import { format as formatTz, utcToZonedTime } from 'date-fns-tz'
import parseISO from 'date-fns/parseISO'
import moment from 'moment-timezone'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import escapeHtml from '../../utils/sanitize'
import { getTimestampFormat } from '../../utils/utils'
import {
  trans,
  WEBHOOKS_DELIVERY_LOG_STATUS_SUCCESS,
  WEBHOOKS_DELIVERY_LOG_STATUS_FAILED,
  WEBHOOKS_DELIVERY_LOG_STATUS_PENDING,
  WEBHOOKS_DELIVERY_LOG_STATUS_SKIPPED,
  WEBHOOKS_DELIVERY_LOG_ATTEMPT_MANUAL,
  WEBHOOKS_DELIVERY_RESEND_ACTION,
  WEBHOOKS_DELIVERY_RESEND_SUCCESS,
  WEBHOOKS_DELIVERY_RESEND_FAILURE,
  WEBHOOKS_DELIVERY_RESEND_FAILURE_CONNECTION,
  WEBHOOKS_DELIVERY_RESEND_ERROR,
  VIEW_MORE
} from '../../translator'

const JSON_HEADERS = { Accept: 'application/json' }
const RESEND_MESSAGE_DISPLAY_MS = 3000

declare const accountDateFormat: string
declare const accountTimezone: string
declare const accountIsUseAmPm: string

interface DeliveryLogAttempt {
  id: string
  can_resend: boolean
  record_id: string
  event_type: string
  attempt_number: number
  display_attempt_number: number
  is_manual: boolean
  status: 'success' | 'failed' | 'pending' | 'skipped'
  response_code: number | null
  occurred_at: string
  created_at: string
}

interface DeliveryLogResponse {
  result: {
    data: DeliveryLogAttempt[]
    meta: {
      current_page: number
      per_page: number
      total: number
    }
  }
}

const STATUS_BADGE_CLASS: Record<DeliveryLogAttempt['status'], string> = {
  success: 'badge-success',
  failed: 'badge-danger',
  pending: 'badge-warning',
  skipped: 'badge-light'
}

const STATUS_LABEL_KEY = {
  success: WEBHOOKS_DELIVERY_LOG_STATUS_SUCCESS,
  failed: WEBHOOKS_DELIVERY_LOG_STATUS_FAILED,
  pending: WEBHOOKS_DELIVERY_LOG_STATUS_PENDING,
  skipped: WEBHOOKS_DELIVERY_LOG_STATUS_SKIPPED
} satisfies Record<DeliveryLogAttempt['status'], unknown>

export default class extends Controller {
  static targets = ['table', 'emptyState', 'eventTypeFilter', 'statusFilter', 'dateFromFilter', 'dateToFilter', 'pagination']

  static values = {
    baseUrl: String
  }

  declare readonly tableTarget: HTMLElement
  declare readonly emptyStateTarget: HTMLElement
  declare readonly eventTypeFilterTarget: HTMLElement
  declare readonly statusFilterTarget: HTMLElement
  declare readonly dateFromFilterTarget: HTMLInputElement
  declare readonly dateToFilterTarget: HTMLInputElement
  declare readonly paginationTarget: HTMLElement
  declare readonly baseUrlValue: string

  private currentPage = 1
  private perPage = 25
  private isClearingFilters = false

  connect (): void {
    this.initializeDateFilters()
    this.load(1).catch(() => undefined)
  }

  onFilterChanged (): void {
    if (this.isClearingFilters) {
      return
    }

    this.load(1).catch(() => undefined)
  }

  clearAllFilters (): void {
    const jquery = (window as unknown as {
      $?: (selector: HTMLElement) => {
        dropdown: (action: string) => unknown
        datepicker: (action: string) => unknown
      }
    }).$

    if (jquery === undefined) {
      return
    }

    this.isClearingFilters = true

    jquery(this.eventTypeFilterTarget).dropdown('clear')
    jquery(this.statusFilterTarget).dropdown('clear')
    jquery(this.dateFromFilterTarget).datepicker('clearDates')
    jquery(this.dateToFilterTarget).datepicker('clearDates')

    this.isClearingFilters = false
    this.load(1).catch(() => undefined)
  }

  paginate (event: { detail?: { page?: number } }): void {
    const requestedPage = Number(event.detail?.page)

    if (!Number.isInteger(requestedPage) || requestedPage < 1 || requestedPage === this.currentPage) {
      return
    }

    this.load(requestedPage).catch(() => undefined)
  }

  private async load (page: number): Promise<void> {
    const response = await fetchWithHandlingLoginRedirect(this.buildUrl(page))
    const body = await response.json() as DeliveryLogResponse

    this.currentPage = body.result.meta.current_page
    this.perPage = body.result.meta.per_page

    this.renderRows(body.result.data)
    this.renderPagination(body.result.meta.total)
  }

  private buildUrl (page: number): string {
    const params = new URLSearchParams()
    params.set('page', String(page))

    const eventType = this.dropdownValue(this.eventTypeFilterTarget)
    if (eventType !== '') {
      params.set('event_type', eventType)
    }

    const status = this.dropdownValue(this.statusFilterTarget)
    if (status !== '') {
      params.set('status', status)
    }

    const dateFrom = this.dateFilterValue(this.dateFromFilterTarget, false)
    if (dateFrom !== '') {
      params.set('from', dateFrom)
    }

    const dateTo = this.dateFilterValue(this.dateToFilterTarget, true)
    if (dateTo !== '') {
      params.set('to', dateTo)
    }

    return `${this.baseUrlValue}?${params.toString()}`
  }

  private dropdownValue (target: HTMLElement): string {
    const jquery = (window as unknown as { $?: (selector: HTMLElement) => { dropdown: (action: string) => string } }).$
    if (jquery === undefined) {
      return ''
    }

    return jquery(target).dropdown('get value')
  }

  private dateFilterValue (target: HTMLInputElement, endOfDay: boolean): string {
    const jquery = (window as unknown as {
      $?: (selector: HTMLElement) => { data: (key: string) => { getDate: () => Date | null } | undefined }
    }).$
    const selectedDate = jquery?.(target).data('datepicker')?.getDate()

    if (selectedDate === null || selectedDate === undefined || Number.isNaN(selectedDate.getTime())) {
      return ''
    }

    const date = moment.tz([
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate()
    ], accountTimezone)

    if (endOfDay) {
      date.add(1, 'day').subtract(1, 'millisecond')
    }

    return date.toISOString()
  }

  private initializeDateFilters (): void {
    const jquery = (window as unknown as {
      $?: (selector: HTMLElement) => {
        datepicker: (options: { autoclose: boolean, format: string, zIndexOffset: string }) => unknown
        on: (event: string, handler: () => void) => unknown
      }
    }).$

    if (jquery === undefined) {
      return
    }

    const options = {
      autoclose: true,
      format: accountDateFormat,
      zIndexOffset: '2000'
    }

    for (const target of [this.dateFromFilterTarget, this.dateToFilterTarget]) {
      const datepicker = jquery(target)
      datepicker.datepicker(options)
      datepicker.on('changeDate', () => { this.onFilterChanged() })
    }
  }

  private renderRows (attempts: DeliveryLogAttempt[]): void {
    this.tableTarget.querySelectorAll('tr[data-delivery-log-row]').forEach(row => { row.remove() })

    if (attempts.length === 0) {
      this.emptyStateTarget.classList.remove('d-none')
      return
    }

    this.emptyStateTarget.classList.add('d-none')

    const rows = attempts.map(attempt => this.renderRow(attempt)).join('')
    this.emptyStateTarget.insertAdjacentHTML('beforebegin', rows)
  }

  private renderRow (attempt: DeliveryLogAttempt): string {
    const badgeClass = STATUS_BADGE_CLASS[attempt.status]
    const statusLabel = trans(STATUS_LABEL_KEY[attempt.status], {}, 'integrations')

    return `
      <tr data-delivery-log-row>
        <td class="priority-1">${escapeHtml(attempt.event_type)}</td>
        <td class="priority-2">${escapeHtml(attempt.record_id)}</td>
        <td class="priority-2">${this.renderAttemptNumber(attempt)}</td>
        <td class="priority-1"><span class="badge ${badgeClass}">${statusLabel}</span></td>
        <td class="priority-2">${attempt.response_code === null ? '—' : attempt.response_code}</td>
        <td class="priority-1">${this.renderDateTime(attempt.occurred_at)}</td>
        <td class="priority-1">${this.renderDateTime(attempt.created_at)}</td>
        <td class="text-right table-no-follow-link">${attempt.can_resend ? this.renderActionsMenu(attempt.id) : ''}</td>
      </tr>
    `
  }

  private renderAttemptNumber (attempt: DeliveryLogAttempt): string {
    if (!attempt.is_manual) {
      return String(attempt.display_attempt_number)
    }

    return escapeHtml(trans(WEBHOOKS_DELIVERY_LOG_ATTEMPT_MANUAL, { '%attempt_number%': attempt.display_attempt_number }, 'integrations'))
  }

  private renderActionsMenu (attemptId: string): string {
    const resendLabel = trans(WEBHOOKS_DELIVERY_RESEND_ACTION, {}, 'integrations')
    const viewMoreLabel = trans(VIEW_MORE, {}, 'actions')

    return `
      <div class="dropdown">
        <button type="button" class="btn btn-sm btn-icon-only text-light" data-toggle="dropdown" data-boundary="window" aria-haspopup="true" aria-expanded="false" aria-label="${escapeHtml(viewMoreLabel)}">
          <i class="fas fa-ellipsis-v" aria-hidden="true"></i>
        </button>
        <div class="dropdown-menu dropdown-menu-right dropdown-menu-arrow">
          <button type="button" class="dropdown-item" data-action="click->delivery-log#resend" data-delivery-log-attempt-id-param="${escapeHtml(attemptId)}">${resendLabel}</button>
        </div>
      </div>
    `
  }

  async resend (event: Event & { params: { attemptId: string }, currentTarget: HTMLElement }): Promise<void> {
    const trigger = event.currentTarget
    if (trigger.classList.contains('disabled')) {
      return
    }

    const { attemptId } = event.params
    const cell = trigger.closest('td')
    trigger.classList.add('disabled')

    try {
      const response = await fetchWithHandlingLoginRedirect(`${this.baseUrlValue}/${attemptId}/resend`, {
        method: 'POST',
        headers: JSON_HEADERS
      })

      if (!response.ok) {
        this.showTransientError(cell, trigger)
        return
      }

      const body = await response.json() as { result: { success: boolean, response_code: number | null } }
      const { success, response_code: responseCode } = body.result

      let message: string
      if (success) {
        message = trans(WEBHOOKS_DELIVERY_RESEND_SUCCESS, {}, 'integrations')
      } else if (responseCode !== null) {
        message = trans(WEBHOOKS_DELIVERY_RESEND_FAILURE, { '%response_code%': String(responseCode) }, 'integrations')
      } else {
        message = trans(WEBHOOKS_DELIVERY_RESEND_FAILURE_CONNECTION, {}, 'integrations')
      }

      this.replaceButtonWithMessage(cell, message)
      setTimeout(() => { this.load(this.currentPage).catch(() => undefined) }, RESEND_MESSAGE_DISPLAY_MS)
    } catch {
      this.showTransientError(cell, trigger)
    }
  }

  /** A real result was returned (success or failure) -- the button's row is resolved; a fresh row/button appears on refresh. */
  private replaceButtonWithMessage (cell: HTMLElement | null, message: string): void {
    if (cell === null) {
      return
    }

    cell.innerHTML = `<span class="text-muted rtext-sm">${escapeHtml(message)}</span>`
  }

  /** The request to Raven itself failed -- nothing changed server-side, so keep the button available to retry. */
  private showTransientError (cell: HTMLElement | null, trigger: HTMLElement): void {
    trigger.classList.remove('disabled')

    if (cell === null) {
      return
    }

    cell.querySelectorAll('[data-delivery-log-resend-error]').forEach(node => { node.remove() })
    cell.insertAdjacentHTML('beforeend', `<span class="text-danger rtext-sm ml-2" data-delivery-log-resend-error>${escapeHtml(trans(WEBHOOKS_DELIVERY_RESEND_ERROR, {}, 'integrations'))}</span>`)
  }

  private renderDateTime (value: string | undefined): string {
    if (value === undefined) {
      return ''
    }

    const date = parseISO(value)

    if (Number.isNaN(date.getTime())) {
      return ''
    }

    const dateFormat = accountDateFormat.replace('mm', 'MM')

    return escapeHtml(formatTz(
      utcToZonedTime(date, accountTimezone),
      `${dateFormat} ${getTimestampFormat(accountIsUseAmPm, false)}`,
      { timeZone: accountTimezone }
    ))
  }

  private renderPagination (total: number): void {
    const totalPages = Math.ceil(total / this.perPage)

    if (totalPages <= 1) {
      this.paginationTarget.innerHTML = ''
      return
    }

    const rangeStart = (this.currentPage - 1) * this.perPage + 1
    const rangeEnd = Math.min(this.currentPage * this.perPage, total)

    this.paginationTarget.innerHTML = `
      <nav aria-label="Webhook delivery log pages">
        <div class="row">
          <div class="col"></div>
          <div class="col mt-1">
            <ul class="pagination justify-content-end mb-0">
              <small class="text-muted vertical-center mr-4">${rangeStart}-${rangeEnd} / ${total}</small>
              ${this.currentPage > 1 ? this.renderPaginationControl(this.currentPage - 1, 'fas fa-angle-left', 'Previous page') : ''}
              <li class="page-item active"><div class="page-link">${this.currentPage}</div></li>
              ${this.currentPage < totalPages ? this.renderPaginationControl(this.currentPage + 1, 'fas fa-angle-right', 'Next page') : ''}
            </ul>
          </div>
        </div>
      </nav>
    `
  }

  private renderPaginationControl (page: number, iconClass: string, label: string): string {
    return `
      <li class="page-item" data-action="click->pagination#goToPage" data-pagination-page-param="${page}">
        <div class="page-link">
          <i class="${iconClass}"></i>
          <span class="sr-only">${escapeHtml(label)}</span>
        </div>
      </li>
    `
  }
}
